import { useState } from "react";
import { Check, Download, History, Paperclip, Pencil, Plus } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampSelection, ChampTexte } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Donnee, Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import {
  CUMULS_COMMANDES,
  cumulCommandes,
  dateCourte,
  etatConditions,
  euros,
  horodatage,
  octets,
  periode,
  tonEtatConditions,
} from "@/lib/libelles";
import {
  api,
  urlContenuDocument,
  type ConditionsCommerciales,
  type ConditionsDeLaFiche,
  type CumulCommandes,
  type EvenementConditions,
  type SaisieConditions,
} from "@/lib/api";

/**
 * La section « Conditions commerciales » de la fiche fournisseur (ticket #76, ADR-0056).
 *
 * Ce que LAB a obtenu d'un fournisseur pour une période de validité : franco de port, frais
 * en dessous, livraison, cumul de commandes. Une version par période ; l'historique est la
 * liste de toutes les versions. Trois droits distincts — ajouter, modifier, valider —, que
 * le serveur vérifie et que l'écran suit pour masquer ses boutons, jamais pour autoriser.
 *
 * L'état se lit en toutes lettres (ADR-0024) : « À valider » veut dire que ces conditions
 * n'alimentent rien tant qu'une personne habilitée ne les a pas validées. Les pièces
 * justificatives passent par l'API des documents (ADR-0011), jamais en statique.
 */

const OPTIONS_CUMUL = Object.entries(CUMULS_COMMANDES).map(([valeur, libelle]) => ({
  valeur,
  libelle,
}));

const LIBELLES_CHAMPS: Record<string, string> = {
  valide_du: "Début de validité",
  valide_au: "Fin de validité",
  seuil_franco_ht: "Seuil de franco",
  frais_sous_seuil_ht: "Frais sous le seuil",
  conditions_livraison: "Conditions de livraison",
  cumul_commandes: "Cumul de commandes",
  statut: "Statut",
};

const LIBELLES_ACTIONS: Record<string, string> = {
  CREATION: "Saisie",
  MODIFICATION: "Modification",
  VALIDATION_CONDITIONS_COMMERCIALES: "Validation",
  AJOUT_JUSTIFICATIF_CONDITIONS: "Justificatif joint",
};

export function ConditionsCommercialesFournisseur({ fournisseurId }: { fournisseurId: string }) {
  const { donnees, erreur, enCours, recharger } = useChargement<ConditionsDeLaFiche>(
    (signal) => api.conditionsCommerciales(fournisseurId, signal),
    [fournisseurId],
  );
  const [ajout, setAjout] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const [historique, setHistorique] = useState(false);

  async function agir(geste: () => Promise<unknown>) {
    setRefus(null);
    try {
      await geste();
      recharger();
      return true;
    } catch (cause) {
      setRefus(messageDe(cause));
      return false;
    }
  }

  return (
    <Carte>
      <EnTeteCarte className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-encre">Conditions commerciales</h2>
        {donnees?.peut_ajouter ? (
          <Bouton taille="compact" variante="discret" onClick={() => setAjout((o) => !o)}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            {ajout ? "Fermer" : "Ajouter des conditions"}
          </Bouton>
        ) : null}
      </EnTeteCarte>

      <CorpsCarte className="flex flex-col gap-4">
        {refus ? <Erreur message={refus} /> : null}
        {erreur ? <Erreur message={erreur} onReessayer={recharger} /> : null}
        {!erreur && enCours && !donnees ? (
          <Chargement message="Chargement des conditions commerciales…" />
        ) : null}

        {ajout && donnees ? (
          <FormulaireConditions
            libelleValider="Enregistrer les conditions"
            onAnnuler={() => setAjout(false)}
            onValider={async (saisie) => {
              if (await agir(() => api.ajouterConditionsCommerciales(fournisseurId, saisie))) {
                setAjout(false);
              }
            }}
          />
        ) : null}

        {donnees && donnees.conditions.length === 0 && !ajout ? (
          <Vide
            titre="Conditions commerciales non renseignées"
            detail="Sans conditions validées, aucun contrôle du franco de port ne s'appuie sur cette fiche."
          />
        ) : null}

        {donnees && donnees.conditions.length > 0 ? (
          <ul className="flex flex-col gap-4">
            {donnees.conditions.map((condition) => (
              <VersionConditions
                key={condition.id}
                condition={condition}
                fournisseurId={fournisseurId}
                peutModifier={donnees.peut_modifier}
                peutValider={donnees.peut_valider}
                peutJoindre={donnees.peut_ajouter || donnees.peut_modifier}
                agir={agir}
              />
            ))}
          </ul>
        ) : null}

        {donnees && donnees.conditions.length > 0 ? (
          <div className="border-t border-filet pt-3">
            <Bouton
              taille="compact"
              variante="discret"
              aria-expanded={historique}
              onClick={() => setHistorique((o) => !o)}
            >
              <History className="h-3.5 w-3.5" aria-hidden />
              {historique ? "Masquer l'historique" : "Afficher l'historique"}
            </Bouton>
            {historique ? <HistoriqueConditions fournisseurId={fournisseurId} /> : null}
          </div>
        ) : null}
      </CorpsCarte>
    </Carte>
  );
}

function VersionConditions({
  condition,
  fournisseurId,
  peutModifier,
  peutValider,
  peutJoindre,
  agir,
}: {
  condition: ConditionsCommerciales;
  fournisseurId: string;
  peutModifier: boolean;
  peutValider: boolean;
  peutJoindre: boolean;
  agir: (geste: () => Promise<unknown>) => Promise<boolean>;
}) {
  const [correction, setCorrection] = useState(false);
  const aValider = condition.etat === "A_VALIDER";

  return (
    <li
      className="flex flex-col gap-3 rounded-[6px] border border-filet p-4"
      aria-label={`Conditions ${periode(condition.valide_du, condition.valide_au)}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-encre">
          {periode(condition.valide_du, condition.valide_au)}
        </p>
        <Etiquette ton={tonEtatConditions(condition.etat)}>{etatConditions(condition.etat)}</Etiquette>
      </div>

      {correction ? (
        <FormulaireConditions
          conditions={condition}
          libelleValider="Enregistrer la modification"
          onAnnuler={() => setCorrection(false)}
          onValider={async (saisie) => {
            if (
              await agir(() =>
                api.corrigerConditionsCommerciales(fournisseurId, condition.id, saisie),
              )
            ) {
              setCorrection(false);
            }
          }}
        />
      ) : (
        <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          <Donnee libelle="Seuil de franco de port">
            {condition.seuil_franco_ht === null
              ? "Pas de franco"
              : `${euros(condition.seuil_franco_ht)} HT`}
          </Donnee>
          <Donnee libelle="Frais sous le seuil">
            {condition.frais_sous_seuil_ht === null
              ? "—"
              : `${euros(condition.frais_sous_seuil_ht)} HT`}
          </Donnee>
          <Donnee libelle="Cumul de commandes">{cumulCommandes(condition.cumul_commandes)}</Donnee>
          <Donnee libelle="Conditions de livraison">
            <span className="whitespace-pre-line">{condition.conditions_livraison ?? "—"}</span>
          </Donnee>
        </dl>
      )}

      <p className="text-xs text-encre-douce">
        {aValider
          ? "Non validées : ces conditions n'alimentent rien tant qu'elles ne sont pas validées."
          : `Validées par ${condition.valide_par ?? "—"} le ${dateCourte(condition.valide_le?.slice(0, 10))}.`}
        {condition.cree_par ? ` Saisies par ${condition.cree_par}.` : ""}
      </p>

      <div>
        <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-encre">
          <Paperclip className="h-3.5 w-3.5 text-encre-douce" aria-hidden />
          Pièces justificatives ({condition.pieces.length})
        </p>
        {condition.pieces.length === 0 ? (
          <p className="text-xs text-encre-douce">
            Aucune pièce : joignez les CGV, l'accord particulier ou le courriel du fournisseur.
          </p>
        ) : (
          <ul className="divide-y divide-filet">
            {condition.pieces.map((piece) => (
              <li key={piece.id} className="flex flex-wrap items-center gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-encre">{piece.nom_fichier_origine}</p>
                  <p className="text-xs text-encre-douce">
                    {octets(piece.taille_octets)} · {horodatage(piece.cree_le)}
                    {piece.depose_par ? ` · joint par ${piece.depose_par}` : ""}
                  </p>
                </div>
                <a
                  href={urlContenuDocument(piece.id)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-[6px] border border-filet bg-white px-3 text-sm font-medium text-encre hover:bg-chene-clair/40"
                >
                  <Download className="h-4 w-4" aria-hidden />
                  Télécharger
                  <span className="sr-only"> {piece.nom_fichier_origine}</span>
                </a>
              </li>
            ))}
          </ul>
        )}
        {peutJoindre ? (
          <label className="mt-2 flex flex-col gap-1 text-xs text-encre-douce">
            Joindre un justificatif
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png,text/plain"
              className="text-sm text-encre"
              onChange={async (evenement) => {
                const fichier = evenement.target.files?.[0];
                evenement.target.value = "";
                if (!fichier) return;
                await agir(() =>
                  api.joindreJustificatifConditions(fournisseurId, condition.id, fichier),
                );
              }}
            />
          </label>
        ) : null}
      </div>

      {peutModifier || (peutValider && aValider) ? (
        <div className="flex flex-wrap gap-2">
          {peutModifier ? (
            <Bouton taille="compact" variante="discret" onClick={() => setCorrection((o) => !o)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              {correction ? "Fermer" : "Modifier"}
            </Bouton>
          ) : null}
          {peutValider && aValider ? (
            <Bouton
              taille="compact"
              onClick={() => void agir(() => api.validerConditionsCommerciales(fournisseurId, condition.id))}
            >
              <Check className="h-3.5 w-3.5" aria-hidden />
              Valider ces conditions
            </Bouton>
          ) : null}
        </div>
      ) : null}
      {peutModifier && correction && condition.statut === "VALIDEE" ? (
        <p className="text-xs text-encre-douce">
          Modifier des conditions validées les renvoie à valider, leur période comprise. Valider la version suivante arrête celle-ci la veille.
        </p>
      ) : null}
    </li>
  );
}

function enMontant(texte: string): number | null {
  const net = texte.trim().replace(",", ".");
  return net === "" ? null : Number(net);
}

function FormulaireConditions({
  conditions,
  libelleValider,
  onAnnuler,
  onValider,
}: {
  conditions?: ConditionsCommerciales;
  libelleValider: string;
  onAnnuler: () => void;
  onValider: (saisie: SaisieConditions) => Promise<void>;
}) {
  const [du, setDu] = useState(conditions?.valide_du ?? "");
  const [au, setAu] = useState(conditions?.valide_au ?? "");
  const [seuil, setSeuil] = useState(conditions?.seuil_franco_ht?.toString() ?? "");
  const [frais, setFrais] = useState(conditions?.frais_sous_seuil_ht?.toString() ?? "");
  const [livraison, setLivraison] = useState(conditions?.conditions_livraison ?? "");
  const [cumul, setCumul] = useState<CumulCommandes>(
    conditions?.cumul_commandes ?? "CUMUL_ACCEPTE",
  );
  const [envoi, setEnvoi] = useState(false);

  return (
    <form
      className="flex flex-col gap-3 rounded-[6px] border border-filet bg-chene-clair/20 p-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onValider({
          valide_du: du,
          valide_au: au || null,
          seuil_franco_ht: enMontant(seuil),
          frais_sous_seuil_ht: enMontant(frais),
          conditions_livraison: livraison.trim() || null,
          cumul_commandes: cumul,
        });
        setEnvoi(false);
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ
          libelle="Valables à partir du"
          type="date"
          required
          value={du}
          onChange={(e) => setDu(e.target.value)}
        />
        <Champ
          libelle="Valables jusqu'au"
          type="date"
          aide="Laissez vide si aucune fin n'est connue."
          value={au}
          onChange={(e) => setAu(e.target.value)}
        />
        <Champ
          libelle="Seuil de franco de port (€ HT)"
          inputMode="decimal"
          aide="Laissez vide si le fournisseur n'a pas de franco."
          value={seuil}
          onChange={(e) => setSeuil(e.target.value)}
        />
        <Champ
          libelle="Frais sous le seuil (€ HT)"
          inputMode="decimal"
          value={frais}
          onChange={(e) => setFrais(e.target.value)}
        />
      </div>
      <ChampSelection
        libelle="Cumul de commandes"
        options={OPTIONS_CUMUL}
        value={cumul}
        onChange={(e) => setCumul(e.target.value as CumulCommandes)}
      />
      <ChampTexte
        libelle="Conditions de livraison"
        rows={3}
        value={livraison}
        onChange={(e) => setLivraison(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !du}>
          {libelleValider}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

function valeurLisible(champ: string, valeur: unknown): string {
  if (valeur === null || valeur === undefined || valeur === "") return "vide";
  if (champ === "valide_du" || champ === "valide_au") return dateCourte(String(valeur));
  if (champ === "seuil_franco_ht" || champ === "frais_sous_seuil_ht") {
    return euros(Number(valeur));
  }
  if (champ === "cumul_commandes") return cumulCommandes(String(valeur));
  if (champ === "statut") return valeur === "VALIDEE" ? "validées" : "à valider";
  return String(valeur);
}

function detailEvenement(evenement: EvenementConditions): string[] {
  if (evenement.action !== "MODIFICATION") return [];
  return Object.entries(evenement.donnees).map(([champ, ecart]) => {
    const { avant, apres } = ecart as { avant: unknown; apres: unknown };
    return `${LIBELLES_CHAMPS[champ] ?? champ} : ${valeurLisible(champ, avant)} → ${valeurLisible(champ, apres)}`;
  });
}

function HistoriqueConditions({ fournisseurId }: { fournisseurId: string }) {
  const { donnees, erreur, enCours, recharger } = useChargement(
    (signal) => api.historiqueConditionsCommerciales(fournisseurId, signal),
    [fournisseurId],
  );
  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (enCours || !donnees) return <Chargement message="Chargement de l'historique…" />;
  return (
    <ol className="mt-2 flex flex-col gap-2" aria-label="Historique des conditions commerciales">
      {donnees.evenements.map((evenement) => (
        <li key={evenement.id} className="text-sm text-encre">
          <span className="font-medium">
            {LIBELLES_ACTIONS[evenement.action] ?? evenement.action}
          </span>
          <span className="text-encre-douce">
            {" "}
            · {horodatage(evenement.cree_le)}
            {evenement.auteur ? ` · ${evenement.auteur}` : ""}
          </span>
          {detailEvenement(evenement).map((ligne) => (
            <p key={ligne} className="text-xs text-encre-douce">
              {ligne}
            </p>
          ))}
        </li>
      ))}
    </ol>
  );
}
